import Image from '@react/shared/components/Image';
import {
  Arts,
  BestCare,
  Business,
  Calendar,
  ChatFill,
  Checkmark,
  HomeSmallStroke,
  List,
  Money,
  MoveIn,
  Offer,
  Search,
  Star,
  Trend,
} from 'sly/icons/index';
import clsxm from 'sly/lib/clsxm';
import { eventNames, useEvents } from 'sly/services/events';

import { ShowcaseSection } from './showcase.types';

const svgMap: { [key: string]: typeof Search } = {
  Search: Search,
  Checkmark: Checkmark,
  Security: Money,
  Star: Star,
  Community: HomeSmallStroke,
  List: List,
  Messages: ChatFill,
  Offer: Offer,
  BestCare: BestCare,
  Arts: Arts,
  Calendar: Calendar,
  Trend: Trend,
  HomeSmallStroke: HomeSmallStroke,
  MoveIn: MoveIn,
  Business: Business,
};

export default function Showcase({
  title,
  description,
  mainClass,
  showcaseClass,
  items,
  startImageOnRight = false,
}: {
  title?: string;
  description?: string;
  mainClass?: string;
  showcaseClass?: string;
  items: ShowcaseSection[];
  startImageOnRight?: boolean;
}) {
  const { events } = useEvents();
  const handleClick = (cta?: {
    text: string;
    link: string;
    eventProps?: {
      text: string;
      location: string;
    };
  }) => {
    if (!cta || !cta?.link || !cta?.eventProps) return;
    if (cta) events.track(eventNames.ButtonClicked, { ...cta.eventProps });
    window.open(cta?.link, '_blank');
  };
  return (
    <main
      className={
        mainClass ||
        'lg:px-auto flex flex-col items-center gap-8 self-stretch py-xxl px-m md:gap-10 md:py-xxxl md:px-l lg:gap-12 lg:py-xxxl'
      }
    >
      <section className='flex flex-col gap-6 md:max-w-[820px]'>
        <span className='flex flex-col items-center gap-4 self-stretch'>
          <h2 className='font-t-xl text-center'>{title}</h2>
          {!!description && (
            <p className='font-b-l text-center'>{description}</p>
          )}
        </span>
      </section>
      <section
        className={
          showcaseClass ||
          'flex w-full flex-col items-start gap-6 md:items-center md:gap-20'
        }
      >
        {items.map((item, index) => (
          <div
            key={index}
            className={clsxm(
              'flex w-full flex-col items-center gap-6 align-baseline md:justify-evenly md:gap-0 lg:w-[64.5rem] lg:justify-center lg:gap-12',
              (index + (startImageOnRight ? 1 : 0)) % 2
                ? 'md:flex-row-reverse'
                : 'md:flex-row'
            )}
          >
            <div className='h-full w-full md:w-[var(--col4)] lg:w-full'>
              <Image
                src={item.image.src}
                alt={item.heading}
                layout='raw'
                width={200}
                height={200}
                className='h-full max-h-[400px] w-full rounded-lg object-contain'
              />
            </div>
            <div className='flex w-full flex-col gap-8 md:w-[var(--col4)] lg:w-full'>
              <div className='flex w-full flex-col gap-4 whitespace-pre-wrap'>
                {item?.heading && <h2 className='font-t-m'>{item.heading}</h2>}
                {typeof item.description === 'string' ? (
                  <p className='font-b-m'>{item.description}</p>
                ) : (
                  item.description
                )}
                {item?.cta && (
                  <button
                    className='provider-cta w-fit'
                    onClick={() => handleClick(item?.cta)}
                  >
                    <p className='font-t-m-azo-xs text-white'>
                      {item?.cta?.text || 'Learn more'}
                    </p>
                  </button>
                )}
              </div>

              {!!item?.ItemList?.length && (
                <div className='flex flex-col justify-start gap-4 self-stretch'>
                  <div className='flex flex-col items-start justify-center gap-4 self-stretch'>
                    {item.ItemList.map((elem, listItemindex) => {
                      const Icon = svgMap[elem.icon];
                      return (
                        <div
                          key={listItemindex}
                          className='flex flex-row items-start align-baseline'
                        >
                          {Icon && <Icon className='mr-s' />}
                          <span className='font-b-m'>{elem.text}</span>
                        </div>
                      );
                    })}
                    {item.banner &&
                      (() => {
                        const Icon = svgMap[item.banner.icon];
                        return (
                          <div className='flex flex-row gap-4 rounded-lg bg-green-lighter-90 p-4 align-baseline'>
                            {Icon && <Icon className='text-green-base' />}
                            <span className='font-b-m whitespace-pre-wrap text-green-base'>
                              {item.banner.text}
                            </span>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </section>
    </main>
  );
}
